import { createContext, useContext, useEffect, useMemo, useState } from "react";
import {
  AUTH_STATE_STATUS,
  getFirebaseWebStatus,
  listenAuthDiagnostics,
  listenAuthState,
  loginWithApplePopup,
  loginWithEmailPassword,
  loginWithGooglePopup,
  logoutCurrentUser,
  refreshCurrentWebUser,
  sendPasswordResetEmailForAddress,
  signupWithEmailPassword,
  updateFirebaseUserDisplayName,
} from "../services/firebaseWeb";
import { presenceOffline, presencePing, reportPresenceDiagnostic } from "../services/presenceRepository";
import { resolveLoginIdentifier } from "../services/authIdentifierApi";
import { getUserNicknameProfile } from "../services/nicknameRepository";
import { completeSignupProfileForCurrentUser, syncAccountProfileViaApi } from "../services/signupProfileApi";
import { isUserEmailVerified, isUserPhoneVerified, syncUserAccountProfile } from "../services/userAccountRepository";

const AuthContext = createContext(null);

const INITIAL_LEVEL_PROFILE = {
  level: 0,
  xpCurrent: 0,
  xpTarget: 1000,
  levelAdvanceBoost: {
    enabled: true,
    thresholdRatio: 0.75,
    approved: true,
  },
  trustProfile: {
    trustScore: 0,
    trustPercent: 0,
    visibility: "AdminOnly",
  },
};

function getReadableErrorMessage(error, fallback) {
  if (error?.code === "firebase/config-missing") {
    const missingKeys = Array.isArray(error.missingKeys) ? error.missingKeys.join(", ") : "";
    return missingKeys
      ? `Firebase yapılandırması eksik: ${missingKeys}`
      : "Firebase yapılandırması eksik. Gerçek veriler yüklenemedi.";
  }
  return error?.message || fallback;
}

function isNicknameIntegrityRace(error) {
  const code = String(error?.code || "");
  return code === "nickname/profile-missing"
    || code === "nickname/reservation-missing"
    || code === "nickname/profile-invalid";
}

function looksLikeEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authStateStatus, setAuthStateStatus] = useState(AUTH_STATE_STATUS.CHECKING);
  const [error, setError] = useState("");
  const [profile, setProfile] = useState(null);
  const [account, setAccount] = useState(null);
  const [sessionVersion, setSessionVersion] = useState(0);

  useEffect(() => {
    const unsubscribe = listenAuthState((nextUser, authState = {}) => {
      if (authState.status === AUTH_STATE_STATUS.UNAVAILABLE || authState.error) {
        const authError = authState.error;
        setAuthStateStatus(AUTH_STATE_STATUS.UNAVAILABLE);
        setError(getReadableErrorMessage(authError, "Oturum durumu doğrulanamadı."));
        setLoading(false);
        return;
      }
      const nextStatus = authState.status || (nextUser ? AUTH_STATE_STATUS.AUTHENTICATED : AUTH_STATE_STATUS.SIGNED_OUT);
      setAuthStateStatus(nextStatus);
      setUser(nextUser ?? null);
      if (!nextUser) {
        setProfile(null);
        setAccount(null);
      }
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  useEffect(() => listenAuthDiagnostics((diagnostic) => {
    const operation = String(diagnostic?.operation || "");
    const isErrorDiagnostic = diagnostic?.severity === "error" || diagnostic?.severity === "critical" || Boolean(diagnostic?.error);
    if (!isErrorDiagnostic) return;
    if (operation === "auth.persistence.set") {
      setAuthStateStatus(AUTH_STATE_STATUS.UNAVAILABLE);
      setError("Giriş kalıcılığı hazırlanamadı. Oturum güvenli şekilde doğrulanamadı.");
      return;
    }
    if (operation === "auth.redirect.result") {
      setError("Yönlendirmeli giriş sonucu doğrulanamadı. Lütfen tekrar giriş yap.");
      return;
    }
    if (operation.startsWith("auth.login") || operation.startsWith("auth.signup") || operation.startsWith("auth.provider")) {
      setError(diagnostic?.error?.message || "Giriş işlemi tamamlanamadı.");
    }
  }), []);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!user?.uid) {
        setProfile(null);
        setAccount(null);
        return;
      }
      try {
        const nextProfile = await getUserNicknameProfile(user.uid, { email: user.email });
        let syncedAccount = null;
        try {
          syncedAccount = await syncUserAccountProfile(user);
        } catch {
          await syncAccountProfileViaApi();
          syncedAccount = null;
        }
        if (active) {
          setProfile(nextProfile || null);
          setAccount(syncedAccount || null);
        }
      } catch (profileError) {
        if (active) {
          setProfile(null);
          setAccount(null);
          if (isNicknameIntegrityRace(profileError)) {
            try {
              await syncAccountProfileViaApi();
            } catch {
              // Signup/profile completion is the blocking path; this read is best-effort.
            }
            return;
          }
          setError(getReadableErrorMessage(profileError, "Profil gerçek veriden yüklenemedi."));
        }
      }
    })();
    return () => {
      active = false;
    };
  }, [sessionVersion, user?.uid]);

  useEffect(() => {
    if (!user?.uid) return undefined;

    let destroyed = false;
    const ping = () => {
      if (destroyed) return;
      presencePing({
        uid: user.uid,
        displayName: user.displayName,
      }).catch((presenceError) => {
        reportPresenceDiagnostic({ operation: "presence.ping", uid: user.uid, error: presenceError });
      });
    };

    ping();
    const interval = window.setInterval(ping, 45_000);
    const onVisibility = () => {
      if (document.visibilityState === "visible") ping();
    };
    const onBeforeUnload = () => {
      presenceOffline({ uid: user.uid }).catch((presenceError) => {
        reportPresenceDiagnostic({ operation: "presence.offline.beforeunload", uid: user.uid, error: presenceError });
      });
    };

    window.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("beforeunload", onBeforeUnload);

    return () => {
      destroyed = true;
      window.clearInterval(interval);
      window.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("beforeunload", onBeforeUnload);
      presenceOffline({ uid: user.uid }).catch((presenceError) => {
        reportPresenceDiagnostic({ operation: "presence.offline.cleanup", uid: user.uid, error: presenceError });
      });
    };
  }, [user?.uid, user?.email, user?.displayName]);

  const nickname = profile?.nickname || "";

  const value = useMemo(() => ({
    user,
    profile,
    account,
    nickname,
    isEmailVerified: isUserEmailVerified(user),
    isPhoneVerified: isUserPhoneVerified(user, account),
    loading,
    authStateStatus,
    isAuthStateVerified: authStateStatus === AUTH_STATE_STATUS.AUTHENTICATED || authStateStatus === AUTH_STATE_STATUS.SIGNED_OUT,
    isAuthUnavailable: authStateStatus === AUTH_STATE_STATUS.UNAVAILABLE,
    error,
    clearError: () => setError(""),
    firebaseStatus: getFirebaseWebStatus(),
    refreshSession: async () => {
      const refreshedUser = await refreshCurrentWebUser();
      setUser(refreshedUser || null);
      setSessionVersion((current) => current + 1);
      return refreshedUser;
    },
    login: async (identifier, password) => {
      setError("");
      try {
        const cleanIdentifier = String(identifier || "").trim();
        const resolved = looksLikeEmail(cleanIdentifier)
          ? { email: cleanIdentifier.toLocaleLowerCase("tr-TR") }
          : await resolveLoginIdentifier(cleanIdentifier);
        const loggedUser = await loginWithEmailPassword(resolved.email, password);
        setUser(loggedUser || null);
        setAuthStateStatus(loggedUser ? AUTH_STATE_STATUS.AUTHENTICATED : AUTH_STATE_STATUS.SIGNED_OUT);
        return loggedUser;
      } catch (authError) {
        setError(authError.message || "Giriş Sırasında Hata Oluştu.");
        throw authError;
      }
    },
    resetPassword: async (email) => {
      setError("");
      try {
        return await sendPasswordResetEmailForAddress(email);
      } catch (authError) {
        setError(authError.message || "Şifre sıfırlama bağlantısı gönderilemedi.");
        throw authError;
      }
    },
    signup: async (email, password, nicknameValue, options = {}) => {
      setError("");
      try {
        const createdUser = await signupWithEmailPassword(email, password);
        setUser(createdUser || null);
        setAuthStateStatus(createdUser ? AUTH_STATE_STATUS.AUTHENTICATED : AUTH_STATE_STATUS.SIGNED_OUT);
        if (nicknameValue) {
          const reserved = await completeSignupProfileForCurrentUser({ nickname: nicknameValue, legalConsent: options.legalConsent });
          await updateFirebaseUserDisplayName(createdUser, reserved.nickname);
          setProfile((current) => ({ ...(current || {}), uid: createdUser.uid, email: createdUser.email, nickname: reserved.nickname }));
        } else {
          await syncAccountProfileViaApi();
        }
        setSessionVersion((current) => current + 1);
        return createdUser;
      } catch (authError) {
        setError(authError.message || "Kayıt sırasında hata oluştu.");
        throw authError;
      }
    },
    loginWithGoogle: async (options = {}) => {
      setError("");
      try {
        const providerUser = await loginWithGooglePopup();
        setUser(providerUser || null);
        setAuthStateStatus(providerUser ? AUTH_STATE_STATUS.AUTHENTICATED : AUTH_STATE_STATUS.SIGNED_OUT);
        if (providerUser?.uid && options.legalConsent?.accepted) {
          if (options.nickname) {
            const reserved = await completeSignupProfileForCurrentUser({ nickname: options.nickname, legalConsent: options.legalConsent });
            await updateFirebaseUserDisplayName(providerUser, reserved.nickname);
            setProfile((current) => ({ ...(current || {}), uid: providerUser.uid, email: providerUser.email, nickname: reserved.nickname }));
          } else {
            await syncAccountProfileViaApi();
          }
          setSessionVersion((current) => current + 1);
        }
        return providerUser;
      } catch (authError) {
        setError(authError.message || "Google Girişi Sırasında Hata Oluştu.");
        throw authError;
      }
    },
    loginWithApple: async (options = {}) => {
      setError("");
      try {
        const providerUser = await loginWithApplePopup();
        setUser(providerUser || null);
        setAuthStateStatus(providerUser ? AUTH_STATE_STATUS.AUTHENTICATED : AUTH_STATE_STATUS.SIGNED_OUT);
        if (providerUser?.uid && options.legalConsent?.accepted) {
          if (options.nickname) {
            const reserved = await completeSignupProfileForCurrentUser({ nickname: options.nickname, legalConsent: options.legalConsent });
            await updateFirebaseUserDisplayName(providerUser, reserved.nickname);
            setProfile((current) => ({ ...(current || {}), uid: providerUser.uid, email: providerUser.email, nickname: reserved.nickname }));
          } else {
            await syncAccountProfileViaApi();
          }
          setSessionVersion((current) => current + 1);
        }
        return providerUser;
      } catch (authError) {
        setError(authError.message || "Apple Girişi Sırasında Hata Oluştu.");
        throw authError;
      }
    },
    logout: async () => {
      setError("");
      try {
        if (user?.uid) {
          try {
            await presenceOffline({ uid: user.uid });
          } catch (presenceError) {
            reportPresenceDiagnostic({ operation: "presence.offline.logout", uid: user.uid, error: presenceError });
          }
        }
        await logoutCurrentUser();
      } catch (authError) {
        setError(authError.message || "Çıkış Sırasında Hata Oluştu.");
        throw authError;
      }
    },
  }), [account, authStateStatus, error, loading, nickname, profile, sessionVersion, user]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside AuthProvider.");
  return context;
}
